/**
 * Lives in the design-system folder rather than next to its container in `app/src/widgets/`
 * to be lazy and not deal with tailwind setup. Move this folder to `app/src/widgets/` once the
 * Clay migration is finished and tailwind works across the Portal.
 */
import React, { useCallback } from 'react';

import { Button, Dialog, IconButton, Typography } from '@bolteu/clay-react';
import { Cross } from '@bolteu/kalep-react-icons';

import illustrationSrc from '../assets/bolt-star-illustration.png';

type DialogChangeEventDetails = Parameters<
  NonNullable<React.ComponentProps<typeof Dialog.Root>['onOpenChange']>
>[1];

export type BoltStarMonthlyUpdateHeader =
  | { kind: 'illustration'; alt: string }
  | {
      kind: 'level';
      /** "Your level for November" */
      caption: string;
      /** "Needs improvement" | "Standard" */
      tierLabel: string;
      wings?: { leftSrc: string; rightSrc: string };
      benefit?: { iconSrc: string; label: string };
    };

export interface BoltStarMonthlyUpdateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAction: () => void;
  header: BoltStarMonthlyUpdateHeader;
  title: string;
  description: string;
  actionLabel: string;
  closeLabel: string;
}

function LevelHeader({
  caption,
  tierLabel,
  wings,
  benefit,
}: Extract<BoltStarMonthlyUpdateHeader, { kind: 'level' }>) {
  return (
    <div className="flex flex-col items-center gap-1 text-center">
      <Typography className="bolt-font-body-m-accent">{caption}</Typography>

      <div className="flex flex-col items-center gap-3">
        <div className="flex items-center gap-1.5">
          {wings ? <img src={wings.leftSrc} alt="" className="h-8" /> : null}
          <Typography className="bolt-font-heading-l-accent ">{tierLabel}</Typography>
          {wings ? <img src={wings.rightSrc} alt="" className="h-8" /> : null}
        </div>

        {benefit ? (
          <div className="flex items-center gap-2">
            <img src={benefit.iconSrc} alt="" className="size-6" />
            <span className="bolt-font-body-s-regular text-secondary">{benefit.label}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function BoltStarMonthlyUpdateDialog({
  isOpen,
  onClose,
  onAction,
  header,
  title,
  description,
  actionLabel,
  closeLabel,
}: BoltStarMonthlyUpdateDialogProps) {
  const handleOpenChange = useCallback(
    (open: boolean, eventDetails: DialogChangeEventDetails) => {
      // block closing via shroud or ESC
      if (!open && eventDetails.reason === 'close-press') {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange} disablePointerDismissal>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup className="w-full max-w-100 shadow-lg">
          <Dialog.Header className="pb-3 pl-6 pr-4 pt-4">
            <Dialog.Close
              render={<IconButton aria-label={closeLabel} icon={<Cross />} size="sm" />}
              className="ml-auto"
            />
          </Dialog.Header>

          <Dialog.Description className="-mt-4 overflow-x-hidden">
            {header.kind === 'illustration' ? (
              <img src={illustrationSrc} alt={header.alt} className="block w-full" />
            ) : (
              <LevelHeader {...header} />
            )}

            {/* eslint-disable-next-line jsx-a11y/heading-has-content -- content added by title component */}
            <Dialog.Title className="bolt-font-heading-xs-accent" render={<h2 />}>
              {title}
            </Dialog.Title>
            <span>{description}</span>
          </Dialog.Description>

          <Dialog.Footer className="shrink-0">
            <Button className="w-full" size="md" variant="primary" onClick={onAction}>
              {actionLabel}
            </Button>
          </Dialog.Footer>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
